import { Label, Tooltip } from "flowbite-react";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import toast from 'react-hot-toast';
import { HiQuestionMarkCircle } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ColorPicker from '../../../components/common/ColorPicker';
import CustomButton from '../../../components/common/CustomButton';
import UnsavedChangesIndicator from '../../../components/common/UnsavedChangesIndicator';
import RichTextEditor from '../../../components/RichTextEditor';
import { useJwt } from "../../../helpers/common";
import { formatDate } from "../../../helpers/data-format";
import { useIntegrationStatus } from "../../../hooks/useIntegrationStatus";
import { useResourceInfo } from "../../../hooks/useResourceInfo";
import { setResourcesbyIds } from '../../../redux/slices/resources';
import { ENTITY, setCurrentEntity } from "../../../redux/slices/timeline";
import {
  useOrganizationService,
  useProjectService,
  useRoadmapService,
  useTemplateService
} from "../../../services";
import HeadlessSelect from "../../../shared/atoms/HeadlessSelect";
import CustomDropdown from "../../../components/common/CustomDropdown";

// Custom styled select component to match React Select appearance
const StyledSelect = ({ id, name, value, onChange, options, placeholder, className = '', error = false }: any) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const selectedOption = options.find((opt: any) => opt.value === value);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                id={id}
                onClick={() => setIsOpen(!isOpen)}
                className={`block w-full min-h-[48px] px-3 py-2 text-left bg-light-surfaceSecondary dark:bg-secondary-dark border border-light-border-default dark:border-hover-dark rounded-xl shadow-sm transition-all duration-200 hover:border-light-border-interactive hover:shadow-md focus:ring-2 focus:ring-primary-light focus:border-primary-light dark:focus:ring-primary-dark dark:focus:border-primary-dark ${className}`}
            >
                <div className="flex items-center justify-between">
                    <span className={`text-base ${selectedOption ? 'text-light-text-primary dark:text-white' : 'text-light-text-muted dark:text-gray-400'}`}>
                        {selectedOption ? selectedOption.label : placeholder}
                    </span>
                    <svg
                        className={`w-5 h-5 text-light-text-secondary dark:text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </button>

            {isOpen && (
                <div className="absolute z-10 w-full mt-1 bg-light-surfaceHover dark:bg-gray-700 border border-light-border-default dark:border-gray-600 rounded-lg shadow-md overflow-hidden">
                    <ul className="py-1 max-h-60 overflow-auto">
                        {options.map((option: any) => (
                            <li
                                key={option.value}
                                onClick={() => {
                                    onChange({ target: { name, value: option.value } });
                                    setIsOpen(false);
                                }}
                                className={`px-3 py-2 cursor-pointer text-base transition-colors duration-150 ${option.value === value
                                    ? 'bg-light-status-info-bg dark:bg-slate-600 text-light-text-primary dark:text-white'
                                    : 'text-light-text-primary dark:text-gray-300 hover:bg-light-interactive-hover dark:hover:bg-gray-600'
                                    }`}
                            >
                                {option.label}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

interface AddUpdateProjectModalProps {
    setShowModal?: any,
    isVisible?: any,
    project?: any
    onProjectUpdate?: any
    type?: string
    onCreate?: (data?: any) => void;
    preventRedirect?: boolean;
}

const CreateEpicNormalModal: React.FC<AddUpdateProjectModalProps> = ({ setShowModal, isVisible = false, project = {}, onProjectUpdate, type, onCreate, preventRedirect = false }) => {
    const JWTdata: any = useJwt()
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { roadmap_id } = useParams();
    const {
        createProject,
        updateProject,
        deleteProjects,
        fetchSingleProject,
        clearSingleProject
    } = useProjectService();
    const { } = useOrganizationService()
    const { fetchSingleRoadmap } = useRoadmapService();
    const { fetchAllTemplates } = useTemplateService();
    const { getResourceName } = useResourceInfo();
    const { shouldShowIntegration } = useIntegrationStatus();

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({
        title: false,
        description: false,
        stage: false,
        start_date: false
    });
    const [apiFieldErrors, setApiFieldErrors] = useState({
        start_date: ''
    });
    const [showStartDateErrorDetails, setShowStartDateErrorDetails] = useState(false);
    let [payloadData, setPayloadData] = useState({ ...project, stage: "executing" })

    const [orgScheduleArr, setOrgScheduleArr] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isDataSyncedRef = useRef<boolean>(false);
    const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { project_id = '' } = project
    const org_id = JWTdata.org_id;
    const user_id = JWTdata.user_id;
    const { singleProjectData, singleProjectLoading: projectLoading } = useSelector((state: any) => state.projects)
    const { templates = [] } = useSelector((state: any) => state.templates);
    const { orgSchedule = [], orgDetails } = useSelector((state: any) => state.organization);
    const { dashboardDetails } = useSelector((state: any) => state.timeline);

    // Get entity type from dashboard
    const entityType = dashboardDetails?.entityType || "Work unit";

    // Helper function to get singular version of entity type
    const getSingularEntityType = (entityType: string) => {
        if (!entityType) return "Work Unit";

        const lower = entityType.toLowerCase();

        // Special case: Tasks should become Task
        if (lower === 'tasks') {
            return "Task";
        }

        // Words ending in -ies (e.g., Stories -> Story, Companies -> Company)
        if (lower.endsWith('ies')) {
            return entityType.slice(0, -3) + 'y';
        }

        // Words ending in -es after s, x, z, ch, sh (e.g., Boxes -> Box, Dishes -> Dish)
        if (lower.endsWith('es') && (lower.endsWith('ses') || lower.endsWith('xes') || lower.endsWith('zes') || lower.endsWith('ches') || lower.endsWith('shes'))) {
            return entityType.slice(0, -2);
        }

        // Words ending in -ves (e.g., Lives -> Life, Knives -> Knife)
        if (lower.endsWith('ves')) {
            return entityType.slice(0, -3) + 'fe';
        }

        // Words ending in -s but not -ss (e.g., Projects -> Project, but not Glass -> Glas)
        if (lower.endsWith('s') && !lower.endsWith('ss')) {
            return entityType.slice(0, -1);
        }

        // Return as-is if no plural pattern matches
        return entityType;
    };

    const singularEntityType = getSingularEntityType(entityType);
    
    const internalPriorities = useMemo(
        () => orgDetails?.priorities ?? [],
        [orgDetails?.priorities]
    );

    const internalPriorityOptions = useMemo(() => {
        return internalPriorities.map((p: any, i: any) => ({
            value: p.id,
            label: `P${i + 1}`,
            color: p.color,
        }));
    }, [internalPriorities]);

    const getSortedTemplates = () => {
        return [...templates].sort((a, b) =>
            a?.project_name?.toLowerCase().localeCompare(b?.project_name?.toLowerCase())
        );
    };


    const unsavedChanges = useMemo(() => {
        if (!project_id || projectLoading || !singleProjectData || Object.keys(singleProjectData).length === 0 || !isDataSyncedRef.current) {
            return false;
        }
        if (!singleProjectData.project_name) {
            return false;
        }

        const original = singleProjectData;
        const current = payloadData;

        if ((original.project_name || '') !== (current.project_name || '')) {
            return true;
        }

        const normalizeDescription = (desc: string) => {
            if (!desc) return '';
            try {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = desc;
                const textContent = tempDiv.textContent || tempDiv.innerText || '';
                let normalized = textContent.replace(/\s+/g, ' ');
                normalized = normalized.replace(/\s+([,.!?;:])/g, '$1');

                return normalized.trim();
            } catch (error) {
                console.warn('DOM-based HTML parsing failed, using regex fallback:', error);
                let normalized = desc.replace(/<[^>]*>/g, '');
                normalized = normalized.replace(/\s+/g, ' ');
                normalized = normalized.replace(/\s+([,.!?;:])/g, '$1');
                return normalized.trim();
            }
        };

        const currentDescRaw = current.project_description || '';
        const initialDescRaw = original.project_description || '';
        const isInitialLoad = !currentDescRaw && initialDescRaw;

        const currentDescNormalized = normalizeDescription(currentDescRaw);
        const initialDescNormalized = normalizeDescription(initialDescRaw);
        const descExactMatch = currentDescNormalized === initialDescNormalized;
        const descContentMatch = currentDescNormalized.replace(/\s/g, '') === initialDescNormalized.replace(/\s/g, '');

        const descriptionChanged = !isInitialLoad && !descExactMatch && !descContentMatch;
        if (descriptionChanged) {
            return true;
        }

        if ((original.stage || '') !== (current.stage || '')) {
            return true;
        }

        const originalDateStr = original.start_date
            ? (original.start_date instanceof Date
                ? formatDate(original.start_date.toISOString())
                : formatDate(String(original.start_date)))
            : null;
        const currentDateStr = current.start_date
            ? (current.start_date instanceof Date
                ? formatDate(current.start_date.toISOString())
                : formatDate(String(current.start_date)))
            : null;
        if (originalDateStr !== currentDateStr) {
            return true;
        }
        if ((original.project_color || '#3b82f6') !== (current.project_color || '#3b82f6')) {
            return true;
        }

        return false;
    }, [project_id, projectLoading, singleProjectData, payloadData]);

    useEffect(() => {
        if (project?.project_id) {
            fetchSingleProject(project?.project_id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);




      useEffect(() => {
        setPayloadData({ start_date: null, stage:"executing" });
        fetchAllTemplates();
        return () => {
          clearSingleProject();
          setPayloadData({});
        };
      }, []);


    useEffect(() => {
        // Collapse details when a new error message arrives or clears
        setShowStartDateErrorDetails(false)
    }, [apiFieldErrors.start_date])


    useEffect(() => {
        const orgScheduleArr: any = []
        if (orgSchedule.length) {
            orgSchedule.forEach((element: { id: any; date: any; }) => {
                orgScheduleArr.push(formatDate(element.date))
            });
            setOrgScheduleArr(orgScheduleArr)
        }
    }, [orgSchedule])



    const handleOnChange = (e: any) => {
        const { name, value } = e.target;
        setPayloadData((pv)=>({...pv,[name]:value}))

        // Clear validation errors when fields are filled
        if (name === 'project_name' && value) {
            setValidationErrors(prev => ({ ...prev, title: false }));
        } else if (name === 'project_description' && value) {
            const strippedContent = value?.replace(/<[^>]*>/g, '').trim();
            if (strippedContent) {
                setValidationErrors(prev => ({ ...prev, description: false }));
            }
        } else if (name === 'stage' && value) {
            setValidationErrors(prev => ({ ...prev, stage: false }));
        }
    }
    const handleOnDateChange = (date: any) => {
        // const data = { ...payloadData, 'start_date': date }
        setPayloadData((pv)=>({...pv,start_date:date}))
        if (date) {
            setValidationErrors(prev => ({ ...prev, start_date: false }));
            setApiFieldErrors(prev => ({ ...prev, start_date: '' }));
        }
    }

    const onSubmit = async () => {
        // Reset validation errors
        setValidationErrors({
            title: false,
            description: false,
            stage: false,
            start_date: false
        });

        if (!payloadData.project_name) {
            setValidationErrors(prev => ({ ...prev, title: true }));
            toast.error(`Please fill Epic name.`);
            return
        }
        if (!payloadData.stage) {
            setValidationErrors(prev => ({ ...prev, stage: true }));
            toast.error("Please select stage.");
            return
        }
        if (!payloadData.start_date) {
            setValidationErrors(prev => ({ ...prev, start_date: true }));
            toast.error("Please select start date.");
            return
        }

        const strippedDescription = payloadData?.project_description?.replace(/<[^>]*>/g, '').trim();
        if (!payloadData?.project_description || !strippedDescription || payloadData?.project_description === '<p></p>' || payloadData?.project_description === '<p><br></p>' || payloadData?.project_description === '<p><br/></p>') {
            setValidationErrors(prev => ({ ...prev, description: true }));
            toast.error("Description can not be null/empty !", { duration: 1000 });
            return
        }

        if (payloadData?.project_description?.length < 15) {
            toast.error("Please add min 15 Char description.");
            return
        }
        const createProjectPayload = {
            org_id,
            user_id,
            "item_type": "epic",
            ...payloadData
        };
        createProjectPayload[`project_description`] = payloadData.project_description;
        if (createProjectPayload?.start_date) {
            createProjectPayload['start_date'] = formatDate(createProjectPayload?.start_date)
        }

        createProjectPayload['project_color'] = payloadData?.project_color || '#3b82f6';
        if (project_id) {
            setIsSubmitting(true);
            const members = payloadData.members || [];
            const UpdateProjectObject: any = {
                project_name: payloadData.project_name,
                stage: payloadData.stage,
                project_description: payloadData.project_description,
                project_color: payloadData.project_color,
                priority: payloadData.priority,
                members,
                org_id,
            };
            if (payloadData?.start_date) {
                UpdateProjectObject['start_date'] = formatDate(payloadData?.start_date)
            }
            dispatch(setResourcesbyIds({ [project_id]: [] }));
            try {
                setApiFieldErrors(prev => ({ ...prev, start_date: '' }));
                const res = await updateProject(project_id, { ...UpdateProjectObject, suppressErrorToast: true, throwOnError: true });
                if (res) {
                    handleProjectUpdate(); // Close only on success
                }
            } catch (error: any) {
                const message = error?.response?.data?.detail || error?.response?.data?.error || error?.message || 'Update failed.';
                setValidationErrors(prev => ({ ...prev, start_date: true }));
                setApiFieldErrors(prev => ({ ...prev, start_date: message }));
            } finally {
                setIsSubmitting(false);
            }
        } else {
            try {
                setIsSubmitting(true);
                const res = await createProject(createProjectPayload, preventRedirect);
                if (onCreate && res) {
                    onCreate(res);
                }
                setShowModal(false);
            } finally {
                setIsSubmitting(false);
            }
        }
    }

    const handleDelete = (status: boolean) => {
        setShowDeleteConfirm(status)
    }

    const submitDelete = async () => {
        if (project_id) {
            setIsDeleting(true);
            try {
                await deleteProjects(project_id, org_id);
                setShowDeleteConfirm(false);
                setShowModal(false);

                if (location.pathname.includes('projects')) {
                    navigate("/workitems");
                } else if ((location.pathname.includes('strategymaps') || location.pathname.includes('roadmaps')) && roadmap_id) {
                    dispatch(setCurrentEntity({
                        entity: ENTITY.ROADMAP,
                        entityId: roadmap_id
                    }))

                    await fetchSingleRoadmap(roadmap_id, false)
                }
            } catch (error) {
                console.error('Error deleting project:', error);
                setShowDeleteConfirm(false);
            } finally {
                setIsDeleting(false);
            }
        }
    }

    const handleProjectUpdate = () => {
        // Call the onProjectUpdate callback if provided (parent handles refresh)
        if (onProjectUpdate) {
            onProjectUpdate();
        }
        handleClose();
        // Dispatch event to reset filters on the projects page
        if (window.location.pathname === "/workitems") {
            try {
                window.dispatchEvent(new CustomEvent('reset-workunits-filters', { detail: { source: 'AddUpdateProjectModal' } }));
            } catch (e) { }
            // Don't call fetchAllProjects/fetchRecentProjects here - parent handles it via onProjectUpdate
        }
    };

    const handleClose = () => {
        clearSingleProject()
        setPayloadData({})
        setShowModal(false)
    }


    const isWeekday = (date: any, org_id: string) => {
        const day = date.getDay();
        const day_date: any = moment(date).format("YYYY-MM-DD");
        if (org_id && localStorage) {
            let workingDay: any = localStorage.getItem("workingDays") ? localStorage.getItem("workingDays") : ""
            let workingDays: any = workingDay ? JSON.parse(workingDay) : {};
            let orgWorkingDays: any = workingDays[org_id] ? workingDays[org_id] : [];
            if (orgScheduleArr.includes(day_date)) {
                return false
            } else if (orgWorkingDays.length) {
                const d = day === 0 ? 7 : day
                return orgWorkingDays.includes(d);
            } else {
                return day !== 0 && day !== 6;
            }
        } else {
            if (orgScheduleArr.includes(day_date)) {
                return false
            } else {
                return day !== 0 && day !== 6;
            }
        }

    };

    return (
      <>
        <div className="h-full w-full flex flex-col bg-light-surface dark:bg-tertiary-dark overflow-hidden animate-fade-in">
          <div className="flex-1 overflow-y-auto bg-light-surface dark:bg-tertiary-dark">
            <div className="px-6 py-4 space-y-6 mb-[100px]">
              {projectLoading ? (
                <div className="space-y-6 animate-pulse">
                  {/* Project Name and Template Row */}
                  <div
                    className={`${!project_id ? "grid grid-cols-2 gap-6" : "space-y-4"}`}
                  >
                    <div className="space-y-2">
                      <Label
                        htmlFor="projectname"
                        className="text-base font-medium text-light-text-primary dark:text-white"
                      >
                        Epic name*
                      </Label>
                      <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                    </div>

                    {!project_id && (
                      <div className="space-y-2">
                        <Label
                          htmlFor="template"
                          className="text-base font-medium text-light-text-primary dark:text-white"
                        >
                          Template
                        </Label>
                        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                      </div>
                    )}
                  </div>

                  {/* Stage and Start Date Row */}
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-base font-medium text-light-text-primary dark:text-white">
                        Epic Stage*
                      </Label>
                      <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                    </div>

                    <div className="space-y-2">
                      <Label
                        className="inline-flex items-center text-base font-medium text-light-text-primary dark:text-white"
                        htmlFor="start_date"
                      >
                        Start Date*
                        <Tooltip
                          placement="top"
                          content={`Default date is Today's date.`}
                        >
                          <HiQuestionMarkCircle className="ml-2 text-lg text-light-text-tertiary dark:text-gray-400" />
                        </Tooltip>
                      </Label>
                      <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                    </div>
                  </div>

                  {/* Description Field */}
                  <div className="space-y-2">
                    <Label
                      className="text-base font-medium text-light-text-primary dark:text-white"
                      htmlFor="description"
                    >
                      Description*
                    </Label>
                    <div className="rounded-xl overflow-hidden border border-light-border-default bg-light-surfaceSecondary dark:border-hover-dark shadow-sm hover:shadow-md transition-shadow duration-200">
                      <RichTextEditor
                        content="<div style='padding: 12px;'><div class='animate-pulse'><div style='height: 20px; background-color: #d1d5db; border-radius: 4px; width: 100%; margin-bottom: 8px;'></div><div style='height: 16px; background-color: #d1d5db; border-radius: 4px; width: 75%; margin-bottom: 8px;'></div><div style='height: 16px; background-color: #d1d5db; border-radius: 4px; width: 60%; margin-bottom: 8px;'></div><div style='height: 16px; background-color: #d1d5db; border-radius: 4px; width: 45%;'></div></div></div>"
                        onChange={() => {}}
                        placeholder={`Enter Epic description...`}
                        disabled={false}
                      />
                    </div>
                  </div>

                  {/* Project Color Field - only for existing projects */}
                  {project_id && (
                    <div className="space-y-2">
                      <Label
                        htmlFor="project_color"
                        className="text-base font-medium text-light-text-primary dark:text-white"
                      >
                        Select {singularEntityType} Color
                      </Label>
                      <div className="flex items-center gap-3 px-3 py-2 border border-light-border dark:border-hover-dark rounded-xl bg-light-surfaceSecondary dark:bg-secondary-dark hover:shadow-md transition-shadow duration-200">
                        <div className="w-20 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                        <div className="text-light-text-secondary dark:text-gray-400 text-sm">
                          Choose a color for your{" "}
                          Epic
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex w-full gap-4">
                    <div className="space-y-2 w-full">
                      <Label
                        htmlFor="roadmapname"
                        className="text-base font-medium text-light-text-primary dark:text-white"
                      >
                        Epic name*
                      </Label>
                      <input
                        type="text"
                        id="roadmapname"
                        className="block w-full px-4 py-3 disabled:pointer-events-none text-light-text-primary border border-light-border-default bg-light-surfaceSecondary text-base focus:ring-2 focus:ring-primary-light focus:border-primary-light placeholder:text-light-input-placeholder transition-all duration-200 dark:bg-secondary-dark dark:border-hover-dark dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark rounded-xl shadow-sm hover:border-light-border-interactive hover:shadow-md"
                        onChange={handleOnChange}
                        name="project_name"
                        placeholder={`Epic name`}
                        defaultValue={payloadData.name}
                        disabled={projectLoading}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-base font-medium text-light-text-primary dark:text-white">
                        Epic Stage*
                      </Label>
                      <HeadlessSelect
                        id="status"
                        className="!w-[160px]"
                        name="stage"
                        value={payloadData.stage || "executing"}
                        onChange={(data) => {
                          console.log(data);
                          handleOnChange(data);
                        }}
                        options={[
                          { value: "planning", label: "Planning" },
                          { value: "executing", label: "Executing" },
                        ]}
                        placeholder={`Choose $Epic status`}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      className="text-base font-medium text-light-text-primary dark:text-white"
                      htmlFor="description"
                    >
                      Description*
                    </Label>
                    <div
                      className={`rounded-xl overflow-hidden border border-light-border-default bg-light-surfaceSecondary dark:border-hover-dark shadow-sm hover:shadow-md transition-shadow duration-200 ${
                        validationErrors.description
                          ? "border-light-status-error-border ring-2 ring-light-status-error-bg dark:border-red-500"
                          : ""
                      }`}
                    >
                      <RichTextEditor
                        content={payloadData?.project_description || ""}
                        onChange={(value) =>
                          handleOnChange({
                            target: { name: "project_description", value },
                          })
                        }
                        placeholder={`Enter Epic description...`}
                        disabled={projectLoading}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="space-y-2 w-full">
                      <Label
                        className="inline-flex items-center text-base font-medium text-light-text-primary dark:text-white"
                        htmlFor="start_date"
                      >
                        Start Date*
                        <Tooltip
                          placement="top"
                          content={`Default date is Today's date.`}
                        >
                          <HiQuestionMarkCircle className="ml-2 text-lg text-light-text-tertiary dark:text-gray-400" />
                        </Tooltip>
                      </Label>
                      <DatePicker
                        id="start_date"
                        className={`block w-full px-4 py-3 text-light-text-primary border border-light-border-default bg-light-surfaceSecondary dark:bg-secondary-dark text-base focus:ring-2 focus:ring-primary-light focus:border-primary-light placeholder:text-light-text-muted dark:border-hover-dark dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark rounded-xl shadow-sm hover:border-light-border-interactive hover:shadow-md transition-all duration-200 ${
                          validationErrors.start_date
                            ? "border-light-status-error-border ring-2 ring-light-status-error-bg dark:border-red-500"
                            : ""
                        }`}
                        selected={
                          payloadData?.start_date !== null &&
                          payloadData?.start_date !== undefined &&
                          payloadData?.start_date !== "None"
                            ? new Date(payloadData?.start_date)
                            : null
                        }
                        onChange={(date: any) => handleOnDateChange(date)}
                        autoComplete="off"
                        filterDate={(date) => isWeekday(date, org_id)}
                        disabled={projectLoading}
                      />
                      {apiFieldErrors.start_date ? (
                        <div className="mt-1">
                          <div className="text-xs text-light-status-error-text dark:text-red-400 leading-snug">
                            Start date cannot be changed because a task starts
                            earlier than the selected date.
                          </div>
                          <button
                            type="button"
                            className="mt-1 text-xs text-red-600 dark:text-red-400 underline decoration-dotted hover:decoration-solid"
                            onClick={() =>
                              setShowStartDateErrorDetails(
                                !showStartDateErrorDetails,
                              )
                            }
                          >
                            {showStartDateErrorDetails
                              ? "Hide details"
                              : "Show details"}
                          </button>
                          {showStartDateErrorDetails && (
                            <div className="mt-1 text-xs text-light-status-error-text dark:text-red-400 whitespace-pre-wrap break-words break-all">
                              {apiFieldErrors.start_date}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>

                    {!project_id && (
                      <div className="space-y-2 w-full">
                        <Label
                          htmlFor="template"
                          className="text-base font-medium text-light-text-primary dark:text-white"
                        >
                          Template
                        </Label>
                        <StyledSelect
                          id="Template"
                          name="template_id"
                          value={payloadData.template_id || ""}
                          onChange={handleOnChange}
                          options={getSortedTemplates().map((template) => ({
                            value: template.template_id,
                            label: template.project_name,
                          }))}
                          placeholder="Select template"
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 w-full">
                    <Label
                      htmlFor="priority"
                      className="text-base font-medium text-light-text-primary dark:text-white"
                    >
                      Priority
                    </Label>
                    <CustomDropdown
                      id="priority"
                      name="priority"
                      value={payloadData.priority || ""}
                      options={internalPriorityOptions}
                      onChange={(val) => handleOnChange({ target: { name: "priority", value: val } })}
                      placeholder="Select Priority"
                      className="!mb-0"
                    />
                  </div>

                  <div className="space-y-2 w-full">
                    <Label
                      htmlFor="project_color"
                      className="text-base font-medium text-light-text-primary dark:text-white"
                    >
                      Select Epic Color*
                    </Label>
                    <div className="flex items-center gap-3 p-4 border border-light-border dark:border-hover-dark rounded-xl bg-light-surfaceSecondary dark:bg-secondary-dark hover:shadow-md transition-shadow duration-200">
                      <ColorPicker
                        value={payloadData?.project_color || "#3b82f6"}
                        onChange={(color) =>
                          handleOnChange({
                            target: { name: "project_color", value: color },
                          })
                        }
                        buttonClassName="w-12 h-12 rounded-lg border-2 border-light-border dark:border-hover-dark  mt-0"
                      />
                      <div className="text-light-text-secondary dark:text-gray-400 text-sm">
                        Choose a color for your Epic
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-shrink-0 px-8 py-4 items-center flex gap-6 border-t border-gray-500 rounded-t-md dark:border-hover-dark bg-light-surfacePrimary dark:bg-tertiary-dark shadow-lg absolute bottom-0 w-full">
              <CustomButton
                variant="cancel"
                onClick={handleClose}
                className="work-unit-drawer__footer-button"
              >
                Back
              </CustomButton>
              <div className="ml-auto flex items-center gap-4">
                {project_id && (
                  <UnsavedChangesIndicator hasUnsavedChanges={unsavedChanges} />
                )}
                <CustomButton
                  onClick={onSubmit}
                  disabled={
                    projectLoading ||
                    isSubmitting ||
                    (project_id && !unsavedChanges)
                  }
                  isLoading={projectLoading || isSubmitting}
                  className="work-unit-drawer__footer-button"
                >
                  {isSubmitting
                    ? project_id
                      ? "Updating..."
                      : "Creating..."
                    : project_id
                      ? "Update"
                      : "Create"}
                </CustomButton>
              </div>
            </div>
          </div>
        </div>
      </>
    );

}

export default CreateEpicNormalModal;